// Kontrol Paneli — Yaklaşan Ödemeler + Tahsilatlar (tarih filtresi + PDF export).
// + Haftalık Nakit Akış Grafiği (7 günlük ödemeler/tahsilatlar özeti, PDF raporu)
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RefreshCw, Download, ChevronLeft, ChevronRight, BarChart3 } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { PageHeader, StatusPill } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/api/client";
import { fmtMoney, fmtDate } from "@/utils/format";
import { exportPDF } from "@/utils/export";
import { useFxRates } from "@/hooks/useFxRates";
import { toast } from "sonner";

const POLL_MS = 60000;

// --- Tarih yardımcıları ---
const isoDay = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
const parseISO = (s) => { const [y,m,d]=String(s).split("-").map(Number); return new Date(y, m-1, d); };
const addDays = (iso, n) => { const d = parseISO(iso); d.setDate(d.getDate()+n); return isoDay(d); };
const TR_SHORT_DAYS = ["Paz","Pzt","Sal","Çar","Per","Cum","Cmt"];

function todayRange() { const iso = isoDay(new Date()); return { from: iso, to: iso }; }
function monthRange() {
  const now = new Date(); const y = now.getFullYear(); const m = now.getMonth();
  return { from: isoDay(new Date(y, m, 1)), to: isoDay(new Date(y, m + 1, 0)) };
}

// Tutar + TL karşılığı (para birimi TRY değilse parantez içinde TL değeri eklenir).
function fmtAmtTL(amount, currency, toTRY) {
  const ccy = String(currency || "TRY").toUpperCase();
  const base = fmtMoney(amount, ccy);
  if (ccy === "TRY" || ccy === "TL") return base;
  const tl = toTRY(amount, ccy);
  return tl ? `${base} (${fmtMoney(tl, "TRY")})` : base;
}

// -----------------------------------------------------------------------------
// Ödeme + Tahsilat listesi hesaplama — hem tablolar hem grafik aynı fonksiyonu
// kullanır. Farklı tarih aralıkları için tekrar çağrılabilir.
// -----------------------------------------------------------------------------
const NOT_PAID_EXCLUDE = new Set(["tahsil_oldu","odendi","paid","cleared","cancelled","iptal","kapali","ciro_edildi"]);
const notPaid = (st) => !NOT_PAID_EXCLUDE.has(String(st||"").toLowerCase());

function buildFlows({ from, to, cheques, bankDeposits, dbs, otherPay, otherColl, subs, cards, posTxs, credits }) {
  const inRange = (d) => d && d >= from && d <= to;
  const payments = [];
  const collections = [];

  // === ÖDEMELER ===
  // Kendi Çeklerimiz
  cheques.filter(c => c.direction === "outgoing" && c.own === true
                      && inRange(c.due_date) && notPaid(c.status))
    .forEach(c => payments.push({
      id: `chk-${c.id}`, type: "Kendi Çekimiz",
      detail: `${c.beneficiary || c.drawer || c.cheque_no || "—"} · ${c.bank_name || ""}`,
      amount: c.amount, currency: c.currency, date: c.due_date, status: c.status,
    }));
  // Krediler
  (credits || []).filter(k => inRange(k.due_date) && k.status !== "odendi" && k.status !== "iptal")
    .forEach(k => payments.push({
      id: `cr-${k.id}`, type: "Kredi",
      detail: `${k.bank_name || "—"} · ${
        ({esit_taksit:"Eşit Taksit", rotatif:"Rotatif", iskonto:"İskonto",
          spot:"Spot", leasing:"Leasing", subvansiyon:"Sübvansiyon"}[k.credit_type] || k.credit_type)
      }${k.interest_rate ? " · %" + k.interest_rate : ""}`,
      amount: k.amount, currency: k.currency, date: k.due_date, status: k.status,
    }));
  dbs.filter(d => inRange(d.due_date) && notPaid(d.status))
    .forEach(d => payments.push({
      id: `dbs-${d.id}`, type: "DBS",
      detail: `${d.supplier_name || "—"} · ${d.invoice_no || ""}`,
      amount: d.amount || d.max_amount, currency: d.currency, date: d.due_date, status: d.status,
    }));
  otherPay.filter(p => inRange(p.payment_date) && notPaid(p.status))
    .forEach(p => payments.push({
      id: `op-${p.id}`, type: "Diğer Ödeme",
      detail: `${p.party_name || "—"}${p.payment_method ? " · " + p.payment_method : ""}${p.notes ? " · " + p.notes : ""}`,
      amount: p.amount, currency: p.currency, date: p.payment_date, status: p.status,
    }));
  subs.filter(s => (s.type === "expense" || !s.type) && inRange(s.next_run) && notPaid(s.status))
    .forEach(s => payments.push({
      id: `sub-${s.id}`, type: "Abonelik",
      detail: `${s.provider || s.name || "—"}${s.subscriber_no ? " · " + s.subscriber_no : ""}`,
      amount: s.amount, currency: s.currency, date: s.next_run, status: s.status,
    }));
  cards.filter(cc => inRange(cc.due_date) && notPaid(cc.status))
    .forEach(cc => payments.push({
      id: `cc-${cc.id}`, type: "Kredi Kartı",
      detail: `${cc.bank_name || "—"} · ${cc.name || ""}${cc.last_four ? " **** " + cc.last_four : ""}`,
      amount: cc.statement_amount, currency: cc.currency, date: cc.due_date, status: cc.status,
    }));

  // === TAHSİLATLAR ===
  bankDeposits.filter(c => notPaid(c.status) && c.expected_collection_date && inRange(c.expected_collection_date))
    .forEach(c => collections.push({
      id: `bnk-${c.id}`, type: "Bankaya Verilen Çek",
      detail: `${c.drawer || c.cheque_no || "—"} · ${c.deposit_bank_name || c.bank_name || ""}`,
      amount: c.amount, currency: c.currency,
      date: c.expected_collection_date, status: c.status,
    }));
  otherColl.filter(p => inRange(p.payment_date) && notPaid(p.status))
    .forEach(p => collections.push({
      id: `oc-${p.id}`, type: "Diğer Tahsilat",
      detail: `${p.party_name || "—"}${p.payment_method ? " · " + p.payment_method : ""}${p.notes ? " · " + p.notes : ""}`,
      amount: p.amount, currency: p.currency, date: p.payment_date, status: p.status,
    }));
  subs.filter(s => s.type === "income" && inRange(s.next_run) && notPaid(s.status))
    .forEach(s => collections.push({
      id: `sub-${s.id}`, type: "Abonelik Geliri",
      detail: `${s.provider || s.name || "—"}`,
      amount: s.amount, currency: s.currency, date: s.next_run, status: s.status,
    }));
  // POS günsonu — kullanıcı `settlement_date` girmişse onu kullan, yoksa T+1 (hafta sonuysa Pazartesi).
  posTxs.filter(t => !t.settled).forEach(t => {
    let iso;
    if (t.settlement_date) {
      iso = t.settlement_date;
    } else {
      const [y, m, d] = String(t.date || "").split("-").map(Number);
      if (!y || !m || !d) return;
      const settleDate = new Date(y, m - 1, d + 1);
      while (settleDate.getDay() === 0 || settleDate.getDay() === 6) {
        settleDate.setDate(settleDate.getDate() + 1);
      }
      iso = isoDay(settleDate);
    }
    if (!inRange(iso)) return;
    collections.push({
      id: `pos-${t.id}`, type: "POS Günsonu",
      detail: `${t.pos_terminal_name || "POS"} · Brüt ${Number(t.gross_amount||0).toLocaleString("tr-TR")}₺ · %${t.commission_rate}`,
      amount: t.net_amount, currency: "TRY", date: iso, status: "pending",
    });
  });

  payments.sort((a,b) => (a.date||"").localeCompare(b.date||""));
  collections.sort((a,b) => (a.date||"").localeCompare(b.date||""));
  return { payments, collections };
}

// -----------------------------------------------------------------------------
export default function DashboardPage() {
  const nav = useNavigate();
  const { toTRY } = useFxRates();
  const [cheques, setCheques] = useState([]);
  const [chartCheques, setChartCheques] = useState([]); // grafik haftası için ayrı fetch
  const [dbs, setDbs] = useState([]);
  const [otherPay, setOtherPay] = useState([]);
  const [otherColl, setOtherColl] = useState([]);
  const [bankDeposits, setBankDeposits] = useState([]);
  const [posTxs, setPosTxs] = useState([]);
  const [subs, setSubs] = useState([]);
  const [cards, setCards] = useState([]);
  const [credits, setCredits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);

  const initialRange = useMemo(todayRange, []);
  const [from, setFrom] = useState(initialRange.from);
  const [to, setTo] = useState(initialRange.to);
  const rangeLabel = from === to ? `Bugün · ${fmtDate(from)}` : `${fmtDate(from)} — ${fmtDate(to)}`;

  // Grafik için ayrı haftalık aralık
  const [chartWeekStart, setChartWeekStart] = useState(initialRange.from);
  const chartWeekEnd = useMemo(() => addDays(chartWeekStart, 6), [chartWeekStart]);

  const load = async () => {
    setLoading(true);
    try {
      const [c, d, op, oc, s, cc, bd, pos, cr] = await Promise.all([
        api.get(`/cheques?from_date=${from}&to_date=${to}&exclude_deposited=false&limit=1000`),
        api.get("/dbs"),
        api.get("/other-payments"),
        api.get("/other-collections").catch(() => ({ data: [] })),
        api.get("/subscriptions"),
        api.get("/credit-cards"),
        api.get("/cheques?direction=incoming&deposited=true&exclude_deposited=false&limit=1000")
          .catch(() => ({ data: [] })),
        api.get("/pos/transactions?limit=2000").catch(() => ({ data: [] })),
        api.get("/credits?limit=1000").catch(() => ({ data: [] })),
      ]);
      setCheques(c.data || []);
      setDbs(d.data || []);
      setOtherPay(op.data || []);
      setOtherColl(oc.data || []);
      setSubs(s.data || []);
      setCards(cc.data || []);
      setBankDeposits(bd.data || []);
      setPosTxs(pos.data || []);
      setCredits(cr.data || []);
      setLastRefresh(new Date());
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, POLL_MS);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to]);

  // Grafik haftası için ayrı cheques fetch (tablo aralığından bağımsız 7 gün)
  useEffect(() => {
    api.get(`/cheques?from_date=${chartWeekStart}&to_date=${chartWeekEnd}&exclude_deposited=false&limit=1000`)
      .then(r => setChartCheques(r.data || []))
      .catch(() => setChartCheques([]));
  }, [chartWeekStart, chartWeekEnd]);

  // TABLO verisi (tarih aralığı: from-to)
  const { payments, collections } = useMemo(
    () => buildFlows({ from, to, cheques, bankDeposits, dbs, otherPay, otherColl, subs, cards, posTxs, credits }),
    [from, to, cheques, bankDeposits, dbs, otherPay, otherColl, subs, cards, posTxs, credits]
  );

  // GRAFİK verisi (tarih aralığı: chartWeekStart..chartWeekEnd)
  const chartData = useMemo(() => {
    const { payments: wp, collections: wc } = buildFlows({
      from: chartWeekStart, to: chartWeekEnd,
      cheques: chartCheques, bankDeposits, dbs, otherPay, otherColl, subs, cards, posTxs, credits,
    });
    // Gün gün pivot — TL karşılıklarına çevir
    const days = [];
    for (let i = 0; i < 7; i++) {
      const iso = addDays(chartWeekStart, i);
      const d = parseISO(iso);
      const shortDay = TR_SHORT_DAYS[d.getDay()];
      const label = `${shortDay} ${String(d.getDate()).padStart(2,"0")}.${String(d.getMonth()+1).padStart(2,"0")}`;
      days.push({ iso, label, "Ödemeler": 0, "Tahsilatlar": 0 });
    }
    const idx = Object.fromEntries(days.map((d,i) => [d.iso, i]));
    for (const p of wp) {
      const i = idx[p.date]; if (i === undefined) continue;
      const tl = toTRY(p.amount, p.currency || "TRY") ?? Number(p.amount || 0);
      days[i]["Ödemeler"] += Number(tl || 0);
    }
    for (const c of wc) {
      const i = idx[c.date]; if (i === undefined) continue;
      const tl = toTRY(c.amount, c.currency || "TRY") ?? Number(c.amount || 0);
      days[i]["Tahsilatlar"] += Number(tl || 0);
    }
    // Recharts'a 2 ondalıklı float olarak ver
    return days.map(d => ({ ...d, "Ödemeler": Math.round(d["Ödemeler"]*100)/100, "Tahsilatlar": Math.round(d["Tahsilatlar"]*100)/100 }));
  }, [chartWeekStart, chartWeekEnd, chartCheques, bankDeposits, dbs, otherPay, otherColl, subs, cards, posTxs, credits, toTRY]);

  const chartTotals = useMemo(() => {
    const pay = chartData.reduce((s,d) => s + d.Ödemeler, 0);
    const col = chartData.reduce((s,d) => s + d.Tahsilatlar, 0);
    return { pay, col, net: col - pay };
  }, [chartData]);

  const pdfCols = [
    { key: "date",   header: "Tarih",      accessor: (r) => fmtDate(r.date) },
    { key: "type",   header: "Tür" },
    { key: "detail", header: "Açıklama" },
    { key: "amount", header: "Tutar", align: "right", accessor: (r) => fmtMoney(r.amount) },
  ];
  const downloadPDF = async (kind) => {
    const list = kind === "payments" ? payments : collections;
    const title = kind === "payments" ? `Yaklasan Odemeler ${from} — ${to}` : `Yaklasan Tahsilatlar ${from} — ${to}`;
    try {
      await exportPDF(title, list, pdfCols);
      toast.success("PDF hazırlandı");
    } catch (e) { toast.error("PDF oluşturulamadı"); console.error(e); }
  };

  const downloadWeeklyPDF = async () => {
    const cols = [
      { key: "label",       header: "Gün" },
      { key: "iso",         header: "Tarih", accessor: (r) => fmtDate(r.iso) },
      { key: "Ödemeler",    header: "Ödemeler (₺)", align: "right", accessor: (r) => fmtMoney(r["Ödemeler"]) },
      { key: "Tahsilatlar", header: "Tahsilatlar (₺)", align: "right", accessor: (r) => fmtMoney(r["Tahsilatlar"]) },
      { key: "net",         header: "Net (₺)", align: "right",
        accessor: (r) => fmtMoney(r["Tahsilatlar"] - r["Ödemeler"]) },
    ];
    const rows = [
      ...chartData,
      // Toplam satırı
      { label: "TOPLAM", iso: "", "Ödemeler": chartTotals.pay, "Tahsilatlar": chartTotals.col },
    ];
    try {
      await exportPDF(`Haftalik Nakit Akis Raporu ${chartWeekStart} — ${chartWeekEnd}`, rows, cols);
      toast.success("Haftalık rapor hazırlandı");
    } catch (e) { toast.error("PDF oluşturulamadı"); console.error(e); }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Kontrol Paneli"
        subtitle={`Yaklaşan ödemeler ve tahsilatlar · ${rangeLabel}`}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
                   className="h-9 w-40" data-testid="dash-date-from"/>
            <span className="text-xs text-muted-foreground">—</span>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)}
                   className="h-9 w-40" data-testid="dash-date-to"/>
            <Button variant="ghost" size="sm" onClick={() => {
              const r = todayRange(); setFrom(r.from); setTo(r.to);
            }} data-testid="dash-today">Bugün</Button>
            <Button variant="ghost" size="sm" onClick={() => {
              const r = monthRange(); setFrom(r.from); setTo(r.to);
            }} data-testid="dash-this-month">Bu Ay</Button>
            <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="dash-refresh">
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`}/>
              Yenile
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-6">
        <UpcomingList title="Yaklaşan Ödemeler" subtitle={`${payments.length} kayıt · ${rangeLabel}`}
          items={payments} testId="dash-payments" tone="red" toTRY={toTRY}
          onEmpty={() => nav("/other-payments")} onExportPDF={() => downloadPDF("payments")}/>
        <UpcomingList title="Yaklaşan Tahsilatlar" subtitle={`${collections.length} kayıt · ${rangeLabel}`}
          items={collections} testId="dash-collections" tone="emerald" toTRY={toTRY}
          onEmpty={() => nav("/bank-cheques")} onExportPDF={() => downloadPDF("collections")}/>
      </div>

      {/* Haftalık Nakit Akış Grafiği */}
      <WeeklyChart
        weekStart={chartWeekStart}
        weekEnd={chartWeekEnd}
        data={chartData}
        totals={chartTotals}
        onWeekChange={setChartWeekStart}
        onExportPDF={downloadWeeklyPDF}
      />

      {lastRefresh && (
        <div className="text-xs text-muted-foreground text-center">
          Son güncelleme: {lastRefresh.toLocaleTimeString("tr-TR")} · Otomatik yenileme: 60sn
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
function UpcomingList({ title, subtitle, items, testId, tone = "emerald", toTRY, onEmpty, onExportPDF }) {
  return (
    <div className="surface" data-testid={testId}>
      <div className="p-4 border-b border-border flex items-center justify-between">
        <div>
          <div className="font-heading font-semibold text-lg">{title}</div>
          <div className="text-xs text-muted-foreground">{subtitle}</div>
        </div>
        {onExportPDF && items.length > 0 && (
          <Button variant="outline" size="sm" onClick={onExportPDF}
                  data-testid={`${testId}-export-pdf`}>
            <Download className="h-4 w-4 mr-2"/> PDF
          </Button>
        )}
      </div>
      {items.length === 0 ? (
        <div className="p-8 text-center text-muted-foreground text-sm">
          <div>Bu ay için kayıt bulunmuyor.</div>
          {onEmpty && (
            <Button variant="link" size="sm" className="mt-2" onClick={onEmpty}>Yeni ekle →</Button>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto -webkit-overflow-scrolling-touch">
          <table className="w-full text-sm min-w-[560px]">
            <thead className="bg-muted/40 border-b border-border">
              <tr>
                <th className="text-left px-4 py-2 label-caps">Tarih</th>
                <th className="text-left px-4 py-2 label-caps">Tür</th>
                <th className="text-left px-4 py-2 label-caps">Açıklama</th>
                <th className="text-right px-4 py-2 label-caps">Tutar</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-b border-border hover:bg-muted/30">
                  <td className="px-4 py-2 tabular text-xs">{fmtDate(it.date)}</td>
                  <td className="px-4 py-2"><StatusPill status={it.type} label={it.type}/></td>
                  <td className="px-4 py-2 text-xs">{it.detail}</td>
                  <td className={`px-4 py-2 text-right tabular font-semibold ${tone === "red" ? "text-red-600" : "text-emerald-600"}`}>
                    {toTRY ? fmtAmtTL(it.amount, it.currency, toTRY) : fmtMoney(it.amount, it.currency)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
function WeeklyChart({ weekStart, weekEnd, data, totals, onWeekChange, onExportPDF }) {
  const shiftWeek = (n) => onWeekChange(addDays(weekStart, n));
  const tlFmt = (v) => Number(v || 0).toLocaleString("tr-TR", { maximumFractionDigits: 0 }) + " ₺";
  const tooltipFmt = (v) => Number(v || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " ₺";

  return (
    <div className="surface" data-testid="dash-weekly-chart">
      <div className="p-4 border-b border-border flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-muted-foreground"/>
          <div>
            <div className="font-heading font-semibold text-lg">Haftalık Nakit Akış</div>
            <div className="text-xs text-muted-foreground">
              {fmtDate(weekStart)} — {fmtDate(weekEnd)} · 7 günlük ödeme/tahsilat dağılımı
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => shiftWeek(-7)}
                  data-testid="chart-prev-week" title="Önceki hafta">
            <ChevronLeft className="h-4 w-4"/>
          </Button>
          <Input type="date" value={weekStart} onChange={(e) => onWeekChange(e.target.value)}
                 className="h-9 w-40" data-testid="chart-week-start"/>
          <Button variant="outline" size="sm" onClick={() => shiftWeek(7)}
                  data-testid="chart-next-week" title="Sonraki hafta">
            <ChevronRight className="h-4 w-4"/>
          </Button>
          <Button variant="ghost" size="sm" onClick={() => onWeekChange(isoDay(new Date()))}
                  data-testid="chart-this-week">Bu Hafta</Button>
          <Button variant="outline" size="sm" onClick={onExportPDF}
                  data-testid="chart-export-pdf">
            <Download className="h-4 w-4 mr-2"/> PDF Rapor
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 px-3 sm:px-4 pt-4">
        <div className="rounded-lg border border-border/60 p-3">
          <div className="label-caps text-[10px]">Toplam Ödeme</div>
          <div className="tabular font-semibold text-red-600 text-lg">{tlFmt(totals.pay)}</div>
        </div>
        <div className="rounded-lg border border-border/60 p-3">
          <div className="label-caps text-[10px]">Toplam Tahsilat</div>
          <div className="tabular font-semibold text-emerald-600 text-lg">{tlFmt(totals.col)}</div>
        </div>
        <div className="rounded-lg border border-border/60 p-3">
          <div className="label-caps text-[10px]">Net Akış</div>
          <div className={`tabular font-semibold text-lg ${totals.net >= 0 ? "text-emerald-600" : "text-red-600"}`}>
            {(totals.net >= 0 ? "+" : "") + tlFmt(totals.net)}
          </div>
        </div>
      </div>

      <div className="px-2 pt-2 pb-4">
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={data} margin={{ top: 10, right: 16, left: 8, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.25}/>
            <XAxis dataKey="label" fontSize={11} tickLine={false} axisLine={{opacity:0.2}}/>
            <YAxis fontSize={11} tickLine={false} axisLine={{opacity:0.2}}
                   tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}k` : String(v)}/>
            <Tooltip formatter={(v) => tooltipFmt(v)}
                     labelFormatter={(l, payload) => {
                       const iso = payload?.[0]?.payload?.iso;
                       return iso ? fmtDate(iso) : l;
                     }}
                     contentStyle={{ fontSize: 12, borderRadius: 8 }}/>
            <Legend wrapperStyle={{ fontSize: 12 }}/>
            <Bar dataKey="Ödemeler"    fill="#ef4444" radius={[3,3,0,0]} maxBarSize={28}/>
            <Bar dataKey="Tahsilatlar" fill="#10b981" radius={[3,3,0,0]} maxBarSize={28}/>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
